import { useState } from "react";
import { DocumentSaveService } from "../data/services/document-save.service";
import {
  DocumentSaveResult,
  UserContext,
  EditableField
} from "../data/models/document-extraction.model";

interface UseDocumentSaveProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Custom hook for handling document save operations
 */
export const useDocumentSave = ({
  onSuccess,
  onError
}: UseDocumentSaveProps = {}) => {
  const [isSaving, setIsSaving] = useState(false);

  const saveDocument = async (
    currentConfig: any,
    editableFields: EditableField[],
    otherFields: any[],
    userContext: UserContext,
    reextractedTokenCost: string | null,
    totalTokenCost: string,
    flowId: string | number,
    docId: string | number
  ): Promise<DocumentSaveResult> => {
    setIsSaving(true);

    try {
      // Create save request
      const saveRequest = DocumentSaveService.createSaveRequest(
        currentConfig,
        editableFields,
        otherFields,
        userContext,
        reextractedTokenCost,
        totalTokenCost,
        flowId,
        docId
      );

      // Perform save operation
      const result = await DocumentSaveService.saveDocument(saveRequest);

      if (result.success && onSuccess) {
        onSuccess();
      } else if (!result.success && onError) {
        onError(result.error || "Unknown error occurred");
      }

      return result;
    } catch (error) {
      const errorMessage = `Save failed: ${(error as Error).message}`;
      if (onError) {
        onError(errorMessage);
      }
      return {
        success: false,
        error: errorMessage
      };
    } finally {
      setIsSaving(false);
    }
  };

  const canSave = (currentConfig: any, userContext: UserContext) => {
    return DocumentSaveService.canSaveDocument(currentConfig, userContext);
  };

  return {
    saveDocument,
    isSaving,
    canSave
  };
};
